import { addEvent, updateUserActivity } from './analyticsSlice';
import { setAuthStatus, clearAuthStatus } from "./authSlice";

const webSocketMiddleware = (store) => {
  let ws = null; // Single WebSocket connection
  let reconnectTimeout = null; // Timeout for reconnection attempts
  const eventQueue = []; // Queue for events while WebSocket is not ready

  const connectWebSocket = (attempt = 1) => {
    const updatedState = store.getState(); 
    const isAnalyticsPage = updatedState.navigation.isAnalyticsPage;

    //console.log("Checking WebSocket connection state: ", { isAnalyticsPage });

    if (!isAnalyticsPage) {
      //console.log("WebSocket not needed. User is not on /analytics");
      return;
    }

    if (ws && ws.readyState !== WebSocket.CLOSED) {
      console.warn('WebSocket is already connected. Skipping initialization.');
      return;
    }

    //console.log('Initializing WebSocket...');
    ws = new WebSocket('wss://www.trustnodes.com/api/websocket');

    ws.onopen = () => {
      //console.log('WebSocket connected');
      
      store.dispatch({ type: 'websocket/connected' });

      // Reset the reconnection attempt counter
      attempt = 1;
      clearTimeout(reconnectTimeout);

      // Flush queued events
      while (eventQueue.length > 0) {
        const event = eventQueue.shift(); // Remove the oldest event
        //console.log('Flushing queued event:', event);
        ws.send(JSON.stringify(event));
      }

      // Send authentication payload
      const userAddressFromQuery = localStorage.getItem('userAddress');
      const network = localStorage.getItem('selectedNetwork') || 'mainnet';
      if (userAddressFromQuery && network) {
        ws.send(
          JSON.stringify({
            type: 'authenticate',
            userAddressFromQuery,
            network,
          })
        );
        //console.log('Authentication payload sent:', { userAddressFromQuery, network });
      }
    };

    ws.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        //console.log('Message received from server:', response);

        // Handle the message locally in Redux
        processWebSocketMessage(response, store);
      } catch (err) {
        console.error('Error processing WebSocket message:', err.message, {
          eventData: event.data,
          error: err,
        });
      }
    };

    ws.onclose = () => {
      //console.warn("WebSocket closed. Checking if reconnection is needed...");
      store.dispatch({ type: "websocket/disconnected" });

      if (store.getState().navigation.isAnalyticsPage) {
        reconnectWebSocket(attempt);
      }
    };

    ws.onerror = () => {
      //console.error('WebSocket error');
      reconnectWebSocket(attempt);
    };  
  };

  const reconnectWebSocket = async (attempt) => {
    if (ws && ws.readyState !== WebSocket.CLOSED) {
      //console.warn('WebSocket is already connected. Skipping reconnection.');
      return;
    }

    if (attempt > 10) {
      //console.warn('Max reconnection attempts reached. Giving up.');
      store.dispatch({ type: 'websocket/disconnected' });
      return;
    }

    store.dispatch({ type: 'websocket/reconnecting' });

    const delay = Math.min(1000 * 2 ** attempt, 600000); 
    reconnectTimeout = setTimeout(() => {
      //console.log(`Reconnecting WebSocket... (Attempt ${attempt})`);
      connectWebSocket(attempt + 1);
    }, delay);
  };

  const processWebSocketMessage = (response, store) => {
    switch (response.type) {
      case "authenticated":
        if (response.status === "success") {
          store.dispatch(
            setAuthStatus({
              isAdmin: response.isAdmin,
              isSubscriber: response.isSubscriber,
              isVisitor: response.isVisitor,
            })
          );
          //console.log("Redux authentication state updated:", response);
        } else {
          //console.warn("Authentication failed:", response.message);
          store.dispatch(clearAuthStatus());
        }
        break;

      case "analyticsUpdate":
        store.dispatch(addEvent(response.analytics));
        store.dispatch(updateUserActivity());
        break;

      default:
        //console.warn("Unrecognized WebSocket message type:", response.type);
    }
  }; 

  return (next) => (action) => {
    switch (action.type) {
      case "navigation/enterAnalyticsPage":
      //console.log("Entered /analytics, initializing WebSocket...");


      setTimeout(() => {
        const updatedState = store.getState(); 
        const isAnalyticsPage = updatedState.navigation.isAnalyticsPage; 

        //console.log("Checking WebSocket connection state AFTER Redux updates:", { isAnalyticsPage });

        if (isAnalyticsPage) {
          connectWebSocket();
        } else {
          //console.warn("Redux state still not updated, WebSocket not connecting.");
        }
      }, 0); // Small delay ensures Redux updates first

      break;

      case "navigation/leaveAnalyticsPage":
        console.log("Left /analytics, disconnecting WebSocket...");
        if (ws) {
          ws.close();
          ws = null;
        }
        break; 

        default:
          break;
       }

    return next(action);
  };
};


export default webSocketMiddleware;
